import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloseCircleOutlined, DeleteOutlined, EditOutlined, EyeOutlined, SettingOutlined } from '@ant-design/icons';
import { Node } from '@prisma/client';
import { Alert, Button, Descriptions, ModalProps, Result, Space, Spin, Tag } from 'antd';
import { DescriptionsItemType } from 'antd/es/descriptions';
import Dropdown, { DropdownButtonProps } from 'antd/es/dropdown';

import { NodeWithDetails } from '@app/domain-t3-interfaces';

import { useDeleteNode, useGetNode } from '../data';
import { CreateForm, EditForm } from '../forms';

export const useNodeActions = () => {
  const [detailsId, setDetailsId] = useState<number | undefined>();
  const [deleteId, setDeleteId] = useState<number | undefined>();

  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editId, setEditId] = useState<number | undefined>();

  const deleter = useDeleteNode();
  const navigate = useNavigate();

  const detailsNode = useGetNode(detailsId ?? 0, { enabled: !!detailsId });
  const deleteNode = useGetNode(deleteId ?? 0, { enabled: !!deleteId });

  const _getSuppliersDescriptionItems = (data?: NodeWithDetails): DescriptionsItemType[] => [
    { key: 'Node', children: data?.name, label: 'Node' },
    {
      key: 'SupplierList',
      children: data?.suppliers?.map((x) => x.supplierName).join(', ') ?? '',
      label: 'Suppliers',
    },
    {
      key: 'ApprovalFlow',
      children: data?.approvalSteps?.map((x) => <Tag key={x.id}>{x.stage.name}</Tag>) ?? '',
      label: 'Approval Flow',
    },
  ];

  const toggleCreateModal = () => {
    setDetailsId(undefined);
    setDeleteId(undefined);
    setEditId(undefined);
    setCreateModalOpen((prevValue) => !prevValue);
  };

  const toggleEditModal = (id?: number) => {
    setDetailsId(undefined);
    setDeleteId(undefined);
    setCreateModalOpen(false);
    setEditId(id);
  };

  const toggleDeleteModal = (id?: number) => {
    setDetailsId(undefined);
    setEditId(undefined);
    setCreateModalOpen(false);
    setDeleteId(id);
  };

  const toggleDetailsModal = (id?: number) => {
    setDeleteId(undefined);
    setEditId(undefined);
    setCreateModalOpen(false);
    setDetailsId(id);
  };

  const getCreateModalProps = (props?: ModalProps): ModalProps => ({
    ...props,
    open: createModalOpen,
    children: <CreateForm onCancel={toggleCreateModal} />,
    onCancel: toggleCreateModal,
    maskClosable: false,
    footer: null,
    width: 800,
    title: 'Register Node',
    destroyOnClose: true,
  });

  const getEditModalProps = (props?: ModalProps): ModalProps => ({
    ...props,
    open: !!editId,
    onCancel: () => {
      toggleEditModal();
    },
    children: (
      <EditForm
        onCancel={() => {
          toggleEditModal();
        }}
        id={editId}
      />
    ),
    maskClosable: false,
    footer: null,
    width: 800,
    title: 'Edit Node',
    destroyOnClose: true,
  });

  const getActionButton = (record: Node, props?: DropdownButtonProps) => (
    <Dropdown.Button
      size="small"
      {...props}
      menu={{
        items: [
          { label: 'Edit', key: 'edit', icon: <EditOutlined /> },
          { label: 'Delete', key: 'delete', icon: <DeleteOutlined /> },
          { label: 'Manage Supplier Contracts', key: 'manage-supplier-contracts', icon: <SettingOutlined /> },
        ],
        onClick: (e) => {
          switch (e.key) {
            case 'edit':
              toggleEditModal(record.id);
              break;
            case 'delete':
              toggleDeleteModal(record.id);
              break;
            case 'manage-supplier-contracts':
              navigateToNodeSupplierContract(record.name);
              break;
            default:
              console.log(`Cannot handle key: ${e.key}`);
              break;
          }
        },
      }}
      onClick={() => toggleDetailsModal(record.id)}
    >
      <Space>
        <EyeOutlined />
        Details
      </Space>
    </Dropdown.Button>
  );

  const navigateToNodeSupplierContract = (nodeName?: string) => {
    navigate(`/admin/node-supplier-contracts?node=${nodeName}`);
  };

  const getDeleteModalProps = (props?: ModalProps): ModalProps => {
    const isSuppliersEmpty = deleteNode.data?.suppliers?.length === 0;

    const onCancel = () => {
      toggleDeleteModal();
      deleter.reset();
    };

    const onOk = () => {
      if (!isSuppliersEmpty) deleteId && navigateToNodeSupplierContract(deleteNode.data?.name);
      if (deleter.isIdle || deleter.isError) {
        deleteId && deleter.mutate(deleteId);
      } else {
        onCancel();
      }
    };

    return {
      centered: true,
      title: 'Are you sure you want to delete this node?',
      ...props,
      children: deleter.isPending ? (
        <Spin style={{ width: '100%' }} />
      ) : deleter.isSuccess ? (
        <Result
          status="success"
          title="Successfully deleted node"
          subTitle={`Node ${deleteNode.data?.name} has been deleted`}
        />
      ) : deleter.isError ? (
        <Result
          status="error"
          title="Error occured when deleting node"
          subTitle={deleter.error.response?.data?.message}
        />
      ) : (
        <>
          <Descriptions
            bordered
            size="small"
            layout="horizontal"
            column={1}
            labelStyle={{ width: 150 }}
            items={_getSuppliersDescriptionItems(deleteNode.data)}
          />
          {!isSuppliersEmpty && (
            <Alert
              message="Node cannot be deleted as it has linked suppliers."
              description="Please delete all the suppliers linked to the node to proceed with deletion."
              type="error"
              showIcon
              style={{ margin: '5px 0' }}
            />
          )}
        </>
      ),
      cancelButtonProps: {
        style: deleter.isSuccess ? { display: 'none' } : undefined,
        disabled: deleter.isPending,
      },
      okButtonProps: {
        icon: isSuppliersEmpty ? deleter.isSuccess ? <CloseCircleOutlined /> : <DeleteOutlined /> : <SettingOutlined />,
        disabled: deleter.isPending,
      },
      okText: isSuppliersEmpty
        ? deleter.isIdle || deleter.isPending
          ? 'Delete'
          : deleter.isError
            ? 'Retry'
            : 'Close'
        : 'Manage Supplier Contracts',
      okType: isSuppliersEmpty ? (deleter.isSuccess ? undefined : 'danger') : 'primary',
      open: !!deleteId,
      width: 800,
      onCancel,
      onOk,
    };
  };

  const getDetailsModalProps = (props?: ModalProps): ModalProps => {
    const footer = [
      <Button icon={<DeleteOutlined />} key="delete" onClick={() => toggleDeleteModal(detailsId)}>
        Delete
      </Button>,
      <Button icon={<EditOutlined />} key="edit" onClick={() => detailsId && toggleEditModal(detailsId)}>
        Edit
      </Button>,
      <Button key="close" onClick={() => toggleDetailsModal()} icon={<CloseCircleOutlined />} type="primary">
        Close
      </Button>,
    ];

    const items = _getSuppliersDescriptionItems(detailsNode.data);

    return {
      centered: true,
      title: `${detailsNode.data?.name} Details`,
      width: 1100,
      ...props,

      children: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Descriptions
            bordered
            size="small"
            layout="horizontal"
            labelStyle={{ width: 150 }}
            column={1}
            items={items}
          />
        </Space>
      ),
      footer,
      open: !!detailsId,
      onCancel: () => toggleDetailsModal(),
    };
  };

  return {
    getActionButton,
    getDeleteModalProps,
    getDetailsModalProps,
    toggleCreateModal,
    toggleEditModal,
    getCreateModalProps,
    getEditModalProps,
  };
};
